// src/components/useA2PStatusPolling.jsx
import { useState, useEffect, useRef } from 'react';
import supabase from '../lib/supabaseClient';

const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const useA2PStatusPolling = (user, onStatusChange) => {
  const [a2pStatus, setA2pStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollIntervalRef = useRef(null);

  const checkA2PStatus = async () => {
    if (!user?.tenant_id) return;

    try {
      console.log('🔍 Polling A2P status...');
      
      const { data, error } = await supabase.functions.invoke('get-a2p-status');
      
      if (error) {
        console.error('❌ Error fetching A2P status:', error);
        return;
      }

      if (!data?.success) {
        console.log('⚠️ A2P status check returned no data');
        return;
      }

      const currentStatus = {
        brand: data.brand,
        campaigns: data.campaigns || []
      };

      setA2pStatus(currentStatus);

      // Get previous snapshot from database
      const { data: snapshot, error: snapshotError } = await supabase
        .from('a2p_status_snapshots')
        .select('*')
        .eq('tenant_id', user.tenant_id)
        .maybeSingle();

      if (snapshotError && snapshotError.code !== 'PGRST116') {
        console.error('❌ Error fetching snapshot:', snapshotError);
      }

      // Check for status changes (skip if no snapshot OR not yet initialized)
      const isFirstTime = !snapshot || !snapshot.is_initialized;
      
      if (isFirstTime) {
        console.log('📸 First time polling - establishing baseline');
      } else if (snapshot) {
        console.log('🔄 Comparing status changes...');
        detectStatusChanges(snapshot, currentStatus);
      }

      // Update snapshot in database with is_initialized flag
      await supabase
        .from('a2p_status_snapshots')
        .upsert({
          tenant_id: user.tenant_id,
          brand_status: currentStatus.brand?.status || null,
          campaigns: currentStatus.campaigns,
          is_initialized: true,
          last_checked_at: new Date().toISOString()
        }, {
          onConflict: 'tenant_id'
        });

      console.log('✅ Snapshot updated in database');

    } catch (error) {
      console.error('❌ A2P polling error:', error);
    }
  };

  const detectStatusChanges = (snapshot, currentStatus) => {
    const changes = [];

    // Check brand status change
    const oldBrandStatus = snapshot.brand_status;
    const newBrandStatus = currentStatus.brand?.status;

    if (oldBrandStatus !== newBrandStatus && newBrandStatus) {
      console.log(`📢 Brand status changed: ${oldBrandStatus} → ${newBrandStatus}`);
      changes.push({
        type: 'brand',
        oldStatus: oldBrandStatus,
        newStatus: newBrandStatus,
        message: getBrandStatusMessage(newBrandStatus)
      });
    }

    // Check campaign status changes
    const oldCampaigns = snapshot.campaigns || [];
    
    currentStatus.campaigns.forEach(newCampaign => {
      const oldCampaign = oldCampaigns.find(c => c.id === newCampaign.id);
      
      if (oldCampaign) {
        // Existing campaign - check for status change
        if (oldCampaign.status !== newCampaign.status) {
          console.log(`📢 Campaign "${newCampaign.campaign_name}" status changed: ${oldCampaign.status} → ${newCampaign.status}`);
          changes.push({
            type: 'campaign',
            campaignName: newCampaign.campaign_name,
            oldStatus: oldCampaign.status,
            newStatus: newCampaign.status,
            message: getCampaignStatusMessage(newCampaign.campaign_name, newCampaign.status)
          });
        }
      } else {
        // New campaign detected
        console.log(`📢 New campaign detected: "${newCampaign.campaign_name}"`);
        changes.push({
          type: 'campaign',
          campaignName: newCampaign.campaign_name,
          oldStatus: null,
          newStatus: newCampaign.status,
          message: `New campaign "${newCampaign.campaign_name}" created with status: ${newCampaign.status}`
        });
      }
    });

    // Trigger callback for each change
    changes.forEach(change => {
      console.log('📢 Triggering notification:', change);
      if (onStatusChange) {
        onStatusChange(change);
      }
    });
  };

  const getBrandStatusMessage = (status) => {
    switch (status) {
      case 'APPROVED':
      case 'VERIFIED':
        return '🎉 Your A2P Brand has been approved!';
      case 'PENDING':
        return '⏳ Your A2P Brand registration is pending review';
      case 'FAILED':
        return '❌ Your A2P Brand registration was rejected';
      default:
        return `Brand status updated: ${status}`;
    }
  };

  const getCampaignStatusMessage = (campaignName, status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
      case 'verified':
        return `✅ Campaign "${campaignName}" is now verified! You can transfer phone numbers.`;
      case 'pending':
        return `⏳ Campaign "${campaignName}" is pending approval`;
      case 'rejected':
        return `❌ Campaign "${campaignName}" was rejected`;
      default:
        return `Campaign "${campaignName}" status: ${status}`;
    }
  };

  // Start polling
  useEffect(() => {
    if (!user?.tenant_id) return;

    console.log('🚀 Starting A2P status polling');
    setIsPolling(true);

    // Check immediately on mount
    checkA2PStatus();

    // Then poll every 5 minutes
    pollIntervalRef.current = setInterval(checkA2PStatus, POLL_INTERVAL);

    // Cleanup
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        console.log('🛑 Stopped A2P status polling');
      }
      setIsPolling(false);
    };
  }, [user?.tenant_id]);

  return {
    a2pStatus,
    isPolling,
    checkNow: checkA2PStatus
  };
};